import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, Numeric } from '@swyftx/aviary/atoms/Typography';
import { NumericProps } from '@swyftx/aviary/atoms/Typography/Numeric/Numeric.styles';
import { EnhancedTooltip } from '@swyftx/aviary/molecules/EnhancedTooltip/EnhancedTooltip';

import { useSwyftxMargin } from 'src/lib/margin';
import { useMarginAccount } from 'src/lib/margin/hooks/account';
import { getMarginRiskColor, getMarginRiskTitle } from 'src/lib/margin/utils';

type Props = {
  size?: NumericProps['size'];
  weight?: NumericProps['weight'];
  className?: string;
};

const MarginAccountHealthMarginStatus: React.FC<Props> = ({ size, weight = 'bold', className }) => {
  const { t } = useTranslation('margin');
  const { usedMargin, marginLevel, isLoading } = useMarginAccount();
  const { isUnderMaintenance } = useSwyftxMargin();

  const title = useMemo(() => getMarginRiskTitle(usedMargin, marginLevel), [marginLevel, usedMargin]);
  const hasValue = useMemo(() => usedMargin && marginLevel, [marginLevel, usedMargin]);

  return (
    <FlexLayout spacing={4} direction='column' alignItems='start' className={className}>
      <EnhancedTooltip
        name={t('accountOverview.status.name')}
        title={t('accountOverview.status.tooltip')}
        align='start'
        side='top'
      >
        <span>
          <Body size='xsmall' color='secondary' underline='dashed'>
            {t('accountOverview.status.title')}
          </Body>
        </span>
      </EnhancedTooltip>
      <Numeric
        loading={isLoading}
        weight={weight}
        size={size}
        color={isUnderMaintenance && !hasValue ? 'primary' : getMarginRiskColor(usedMargin, marginLevel)}
      >
        {isUnderMaintenance && !hasValue
          ? t('maintenance.labels.unavailable')
          : t(`accountHealth.riskRating.status.${title}`)}
      </Numeric>
    </FlexLayout>
  );
};

export { MarginAccountHealthMarginStatus };
